import { IContentControlStudioViewModel } from 'app/js/src/domain/content-control/studio';
import { IContentControlStudioTimeline } from "../content-control-studio-timeline/i-content-control-studio-timeline";
import { IStudioViewModelContext } from "./i-studio-view-model-context";

export class StudioPlaybackController {
  isAudioMuted = false;
  isAudioForcedMuted = false;
  selectedAudioOption = IContentControlStudioViewModel.VK;
  availableAudioTracks: { [lang: string]: MediaElementAudioSourceNode } = {};

  private _setupAudioContextFn: () => void = () => {};

  constructor(
    private readonly _context: IStudioViewModelContext,
    private readonly _timeline: IContentControlStudioTimeline,
  ) {}

  setSetupAudioContextFn(fn: () => void): void {
    this._setupAudioContextFn = fn;
  }

  get isVideoPaused(): boolean {
    return this._context.getVideoSource()?.paused ?? true;
  }

  get isVideoAudioTrack(): boolean {
    return IContentControlStudioViewModel.VK === this.selectedAudioOption;
  }

  play(): void {
    this._setupAudioContextFn?.();
    const timeline = this._timeline;
    const video = this._context.getVideoSource();
    if (timeline && video) {
      const currentFrameSecs = timeline.currentVideoPlayTimeSecs();
      const cutEvent = timeline.getCutSegmentAtSpecificTime(currentFrameSecs);
      if (cutEvent) {
        console.log("studio-playback-controller->play->cutEvent", cutEvent.startsAtMs, cutEvent.endsAtMs, (currentFrameSecs * 1000));
        const seekTime = timeline.getSeekTimeAfterCutEnd(cutEvent.endsAtMs);

        timeline.seekToVideoPlayerRelativeTimeInSecs(seekTime);
      }
    }
    video?.play().catch(console.error);
  }

  pause(): void {
    this._context.getVideoSource()?.pause();
  }

  changePlaybackSpeed(change: number): void {
    const video = this._context.getVideoSource();
    if (video) {
      if (change == 0) {
        video.playbackRate = 1.0;
      } else {
        video.playbackRate = Math.min(16, Math.max(0.1, video.playbackRate + change));
      }
      console.log("Playback speed:", video.playbackRate);
    }
  }

  frameUp(multiply: number = 1): void {
    this.pause();
    const timeline = this._timeline;
    const currentFrame = timeline.currentVideoPlayTimeSecs();
    const nextFrame = timeline.oneFrameSecs * multiply;
    let newTime = +(currentFrame + nextFrame).toPrecision(12);
    const cutEvent = timeline.getCutSegmentAtSpecificTime(newTime);
    if (cutEvent) newTime = timeline.getSeekTimeAfterCutEnd(cutEvent.endsAtMs);
    timeline.seekToVideoPlayerRelativeTimeInSecs(newTime);
  }

  frameDown(multiply: number = 1): void {
    this.pause();
    const timeline = this._timeline;
    const currentFrame = timeline.currentVideoPlayTimeSecs();
    const nextFrame = timeline.oneFrameSecs * multiply;
    let newTime = +(currentFrame - nextFrame).toPrecision(12);
    const cutEvent = timeline.getCutSegmentAtSpecificTime(newTime);
    if (cutEvent) {
      newTime =
        timeline.getSeekTimeAfterCutEnd(cutEvent.startsAtMs) -
        2 * timeline.oneFrameSecs;
    }
    timeline.seekToVideoPlayerRelativeTimeInSecs(newTime);
  }

  mute(event: Event): boolean {
    event.stopPropagation();
    event.preventDefault();
    this.isAudioForcedMuted = true;
    this.toggleMute(true);
    return true;
  }

  unmute(event: Event): boolean {
    event.stopPropagation();
    event.preventDefault();
    this.isAudioForcedMuted = false;
    this.toggleMute(true);
    return true;
  }

  toggleMute(force: boolean = false): void {
    this.isAudioMuted = force ? this.isAudioForcedMuted : !this.isAudioMuted;
    const video = this._context.getVideoSource();
    if (this.selectedAudioOption === IContentControlStudioViewModel.VK) {
      video.muted = this.isAudioMuted;
    } else {
      this.availableAudioTracks[this.selectedAudioOption].mediaElement.muted = this.isAudioMuted;
    }
  }

  toggleMuteKey(): void {
    this.isAudioForcedMuted = !this.isAudioForcedMuted;
    this.toggleMute();
  }
}
